<!-- CWS-1837: masking is decided by the environment on the server; the tab can only
     say which way it goes, so it is a badge, not a switch, and it shows on every pane. -->
<div
  class="pii-masking"
  [attr.data-tone]="piiMasking.tone"
  role="status"
  data-testid="pii-masking-badge"
>
  <span class="pill" [attr.data-tone]="piiMasking.tone">{{ piiMasking.label }}</span>
  <span class="pii-masking__detail">{{ piiMasking.detail }}</span>
</div>

@if (view === 'system' && openAdapter) {
  <app-vendor-system
    [companyId]="companyId"
    [adapter]="openAdapter"
    [system]="openSystem"
    [companyName]="companyName"
    (back)="backToList()"
  ></app-vendor-system>
} @else if (view === 'file') {
  <div class="pane-back">
    <button type="button" class="link-btn" (click)="backToList()">← Customer migration</button>
  </div>
  <!-- fileOnly keeps the vendor sources out of this pane: vendors are managed as
       systems above, not chosen as an import source. -->
  <app-import-data [companyId]="companyId" [fileOnly]="true"></app-import-data>
} @else {
  <div class="migration-card">
    <div class="card-header">
      <div class="card-header-text">
        <h2 class="card-title">Customer migration</h2>
        <p class="card-description">
          Every system here is temporary — a migration is meant to end.
        </p>
      </div>
      <!--
        Hidden while the list is empty: the empty state owns the call to action
        there, and two identical Add buttons on one screen is worse than none.
      -->
      @if (!loading && systems.length && addableAdapters.length) {
        <app-add-button buttonText="Add a system" (buttonClick)="togglePicker()"></app-add-button>
      }
    </div>

    <div class="card-body">
      @if (loadError) {
        <div class="alert alert-warning" role="status">{{ loadError }}</div>
      }

      @if (loadDegraded) {
        <div class="alert alert-warning" role="status">
          Some of this car wash’s migration state could not be loaded, so this list may be
          incomplete. Reopen the tab to try again.
        </div>
      }

      @if (loading) {
        <p class="muted">Loading this car wash’s migration state…</p>
      } @else if (!systems.length && loadDegraded) {
        <!-- Never the "Nothing connected yet" pitch on top of a failed load: for a
             car wash that does have bridges, that reads as a statement of fact. -->
        <p class="muted">The list could not be loaded.</p>
      } @else if (!systems.length) {
        <div class="empty-state">
          <div class="empty-icon" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <ellipse cx="12" cy="6" rx="7.5" ry="3" stroke="currentColor" stroke-width="1.7" />
              <path
                d="M4.5 6v12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3V6"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
              <path
                d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="empty-copy">
            <span class="empty-title">Nothing connected yet</span>
            <p class="empty-text">
              Connect the system this car wash runs today and we will pull their members over,
              keeping it current until they cut over.
            </p>
          </div>
          <div class="empty-actions">
            @if (addableAdapters.length) {
              <button type="button" class="primary-btn" (click)="togglePicker()">
                Add a system
              </button>
            }
            <button type="button" class="secondary-btn" (click)="openFileImport()">
              Import from a file instead
            </button>
          </div>
        </div>
      } @else {
        <section class="section">
          <!-- Same structure as the other admin lists (audit log, company list):
               a {name}-table-wrap around a plain semantic table. Fixed columns are
               what makes two bridges comparable at a glance. -->
          <div class="system-table-wrap">
            <table class="system-table">
              <thead>
                <tr>
                  <th>System</th>
                  <th>Status</th>
                  <th>Last sync</th>
                  <th>Plans</th>
                  <th class="system-table__action-head">
                    <span class="system-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (system of systems; track trackByVendor($index, system)) {
                  <tr [attr.data-tone]="system.tone">
                    <td class="system-table__name">{{ system.displayName }}</td>
                    <td>
                      <span class="pill" [attr.data-tone]="system.tone">
                        {{ system.statusLabel }}
                      </span>
                    </td>
                    <td [attr.data-tone]="system.lastSync.tone || 'neutral'">
                      {{ system.lastSync.value }}
                    </td>
                    <td [attr.data-tone]="system.plans.tone || 'neutral'">
                      {{ system.plans.value }}
                    </td>
                    <td class="system-table__actions">
                      <button
                        type="button"
                        class="btn btn-outlined btn-small"
                        (click)="openSystemDetail(system.vendor)"
                      >
                        {{ system.isLive ? 'Manage' : 'View record' }}
                      </button>
                      @if (system.offerCutOver) {
                        <button
                          type="button"
                          class="btn btn-primary btn-small"
                          (click)="openSystemDetail(system.vendor)"
                        >
                          Cut over…
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (historyTruncated) {
            <!-- The window is one page of history. A system this car wash has already
                 cut over from has no credentials left, so history is the only thing
                 that lists it — and older runs fall outside the window. Disclosed
                 rather than silently omitted. -->
            <p class="field-hint">
              Older imports are outside this view, so a system already cut over from may not be
              listed.
            </p>
          }
        </section>

        <div class="divider"></div>

        <section class="section file-section">
          <div class="section-heading">
            <h3 class="section-title">No system to connect to?</h3>
            <p class="section-subtitle">
              A one-off load from a CSV or Google Sheet. Nothing stays connected afterwards.
            </p>
          </div>
          <button type="button" class="secondary-btn" (click)="openFileImport()">
            Import from a file
          </button>
        </section>
      }
    </div>
  </div>

  @if (showPicker) {
    <!-- Transient by design: opening this persists nothing. A credential row is
         the only persistence there is, so picking here goes straight to Connect
         and saving credentials is what makes the system real. -->
    <div class="picker-backdrop" (click)="togglePicker()"></div>
    <div class="picker" role="dialog" aria-modal="true" aria-labelledby="picker-title">
      <div class="picker-head">
        <h3 class="picker-title" id="picker-title">Which system does this car wash run?</h3>
        <p class="picker-subtitle">
          Pick one to sign in to it. Nothing is saved until the credentials are.
        </p>
      </div>
      <div class="picker-list">
        @for (adapter of addableAdapters; track trackByName($index, adapter)) {
          <button type="button" class="picker-option" (click)="startAdding(adapter)">
            <span class="picker-option-text">
              <span class="picker-option-name">{{ adapter.displayName || adapter.name }}</span>
              <span class="picker-option-fields">
                @for (field of adapter.credentialFields; track field.key) {
                  {{ field.label }}{{ $last ? '' : ' · ' }}
                }
              </span>
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        }
      </div>
      <div class="picker-foot">
        <p class="field-hint">
          This list is whatever the backend reports, minus what this car wash already has. A new
          adapter appears here the moment it ships — no admin-panel release.
        </p>
        <button type="button" class="secondary-btn" (click)="togglePicker()">Cancel</button>
      </div>
    </div>
  }
}
